import { useContext, useEffect, useMemo } from 'react';

import { GRUSHKA_ORGANIZATION_ID } from '@app/access/constants/organizations';
import { ACTIVE, READY } from '@app/constants/integrationStatuses';
import { useUserPrint } from '@app/hooks';
import { IntegrationsContext } from '@app/providers';
import { integrationActionLog } from '@app/services/__global__/integrationActionLog';

interface UseIntegrationLogRecordingParams {
  /** Интеграции филиала — те же, что показаны в таблице реестра. */
  integrations?: Array<{ Status?: string | null }> | null;
}

/**
 * Ведёт журнал действий, пока открыт реестр интеграций, и решает, показывать
 * ли кнопку выгрузки (us/11024).
 *
 * Запись включается всегда, а не только когда кнопка видна: проблема может
 * появиться в ходе работы, и к моменту её появления история действий уже
 * должна быть собрана.
 */
export const useIntegrationLogRecording = ({
  integrations,
}: UseIntegrationLogRecordingParams = {}) => {
  const {
    params: { Database },
  } = useUserPrint();
  const { setIsExportLogVisible } = useContext(IntegrationsContext);

  useEffect(() => {
    integrationActionLog.start();

    return () => {
      integrationActionLog.stop();
    };
  }, []);

  const hasProblemIntegrations = useMemo(
    () =>
      Boolean(
        integrations?.some(
          ({ Status }) => Status !== ACTIVE && Status !== READY
        )
      ),
    [integrations]
  );

  // «Грушке» кнопка нужна во всех филиалах: там разбор проблем идёт
  // постоянно, и ждать, пока интеграция сломается, смысла нет.
  const isVisible =
    Database === GRUSHKA_ORGANIZATION_ID || hasProblemIntegrations;

  // Саму кнопку рисует шапка страницы — рядом с «Добавить интеграцию»;
  // сюда список интеграций не доходит, поэтому признак кладём в контекст.
  useEffect(() => {
    setIsExportLogVisible(isVisible);

    return () => {
      setIsExportLogVisible(false);
    };
  }, [isVisible, setIsExportLogVisible]);

  return { isVisible, hasProblemIntegrations };
};
